import React, { useState, useEffect, useCallback } from "react";
import { Container, Button, Modal, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import moment from "moment";
import brandAPI from "../../../../services/brandapi";
import { showConfirm } from "react-confirm-prompt";
import DynamicBreadcrumb from "../../../header/breadcrumb";
import BrandViewModal from "./BrandViewModal";
import BrandCreateForm from "./BrandCreateForm";
import { CatalogHeader, CatalogToolbar, CatalogStatus } from "../../../../components/catalog/CatalogUI";

const AllBrands = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [refreshTable, setRefreshTable] = useState(false);
    const [brandId, setBrandId] = useState(null);
    const [viewModal, setViewModal] = useState(false);
    const [createModal, setCreateModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedBrandId, setSelectedBrandId] = useState(null);

    // 🆕 Reason modal state
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [reasonText, setReasonText] = useState("");
    const [reasonErrors, setReasonErrors] = useState({});
    const [selectedStatus, setSelectedStatus] = useState(null);

    const fetchBrands = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const response = await brandAPI.getAllBrands({
                limit: pageSize,
                page,
                sortModel,
                filterModel
            });

            const formatted = response.data.result.map((brand) => ({
                id: brand.id,
                name: brand.name,
                image: brand.image,
                status: brand.status,
                createdAt: moment(brand.createdAt).format("YYYY-MM-DD HH:mm"),
            }));

            setRows(formatted);
            setTotalCount(response.data.totalRecords);
        } catch (error) {
            console.error("Failed to load brands", error);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, sortModel, filterModel, refreshTable]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const handleStatusToggle = (id, status) => {
        const newStatus = status === 1 ? 0 : 1;
        showConfirm("Change status?", {
            description: `Do you want to ${newStatus === 1 ? "activate" : "deactivate"} this brand?`,
            type: "warning"
        }).then((confirm) => {
            if (confirm) {
                if (newStatus === 0) {
                    setSelectedBrandId(id);
                    setSelectedStatus(newStatus);
                    setShowReasonModal(true);
                } else {
                    updateBrandStatus(id, newStatus);
                }
            }
        });
    };

    const updateBrandStatus = async (id, status, reason = "") => {
        try {
            await brandAPI.updateBrandStatus({ id, status, reason });
            setRefreshTable((prev) => !prev);
            setShowReasonModal(false);
            setReasonText("");
        } catch (error) {
            console.error("Failed to update brand status", error);
        }
    };

    const handleReasonSubmit = () => {
        if (!reasonText.trim()) {
            setReasonErrors({ reasonText: "Reason is required" });
            return;
        }
        setReasonErrors({});
        updateBrandStatus(selectedBrandId, selectedStatus, reasonText);
    };

    const columns = [
        { field: "id", headerName: "ID", width: 70 },
        {
            field: "image",
            headerName: "Image",
            width: 100,
            renderCell: (params) => (
                <img
                    src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${params.value}`}
                    alt="brand"
                    width={50}
                />
            ),
        },
        { field: "name", headerName: "Brand Name", flex: 1 },
        { field: "createdAt", headerName: "Created At", flex: 1 },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <CatalogStatus active={Number(params.value) === 1} onClick={() => handleStatusToggle(params.id, params.value)} />
            )
        },
        {
            field: "action",
            headerName: "Actions",
            flex: 1,
            renderCell: (params) => (
                <div className="d-flex gap-2">
                    <Button size="sm" onClick={() => openEditModal(params.id)}>Edit</Button>
                    <Button size="sm" variant="info" onClick={() => openViewModal(params.id)}>View</Button>
                </div>
            )
        }
    ];

    const openViewModal = (id) => {
        setSelectedBrandId(id);
        setViewModal(true);
    };

    const openEditModal = (id) => {
        setEditingId(id);
        setCreateModal(true);
    };

    const openCreateModal = () => {
        setEditingId(null);
        setCreateModal(true);
    };

    return (
        <Container fluid className="catalog-page">
            <DynamicBreadcrumb pathArray={[{ name: "Brands", url: null }]} />
            <CatalogHeader title="Brand Management" description="Find brands, update details and manage availability." count={totalCount}
                actions={<Button onClick={openCreateModal}>+ Add Brand</Button>} />

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={totalCount}
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    onSortModelChange={setSortModel}
                    onFilterModelChange={(model) => { setFilterModel(model); setPaginationModel((prev) => ({ ...prev, page: 0 })); }}
                    slots={{ toolbar: CatalogToolbar }}
                    getRowHeight={() => "auto"}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableColumnFilter
                    disableRowSelectionOnClick
                    slotProps={{
                        toolbar: { showQuickFilter: true },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>

            {viewModal && selectedBrandId && (
                <BrandViewModal
                    show={viewModal}
                    brandId={selectedBrandId}
                    onHide={() => setViewModal(false)}
                />
            )}

            {createModal && (
                <BrandCreateForm
                    show={createModal}
                    editingId={editingId}
                    onHide={() => { setCreateModal(false); setEditingId(null); }}
                    onSuccess={() => setRefreshTable(!refreshTable)}
                />
            )}



            {/* 🆕 Reason Modal */}
            <Modal show={showReasonModal} onHide={() => setShowReasonModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Enter Reason</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="reasonTextArea">
                        <Form.Label>Reason for deactivating brand</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={reasonText}
                            onChange={(e) => setReasonText(e.target.value)}
                            isInvalid={!!reasonErrors.reasonText}
                        />
                        <Form.Control.Feedback type="invalid">
                            {reasonErrors.reasonText}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleReasonSubmit}>Submit</Button>
                    <Button variant="secondary" onClick={() => setShowReasonModal(false)}>Cancel</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AllBrands;
